import { supabase } from '../lib/supabaseClient';
import type { RootCauseAnalysis } from '../types/issue';
import {
  assertHypothesisOnIssue,
  evidenceUploadBytes,
  EVIDENCE_STAGES,
  evidenceKindForMime,
  evidenceStoragePath,
  ISSUE_EVIDENCE_BUCKET,
  type EvidenceStage,
} from '../utils/issueEvidence';

const SIGNED_URL_SECONDS = 60 * 10;

export interface IssueEvidenceRecord {
  id: string;
  issue_id: string;
  organization_id: string;
  stage: EvidenceStage;
  hypothesis_id: string | null;
  name: string;
  kind: 'image' | 'file';
  mime_type: string;
  storage_path: string;
  caption: string | null;
  created_by: string | null;
  created_at: string;
}

/**
 * Apaga os arquivos e as linhas de evidência de um problema.
 * A issue só pode ser excluída depois disso: a FK impede a exclusão enquanto a linha existir,
 * e o Storage não é alcançado por trigger SQL.
 */
export async function removeIssueEvidence(organizationId: string, issueId: string): Promise<void> {
  const { data, error } = await supabase
    .from('issue_evidence')
    .select('storage_path')
    .eq('organization_id', organizationId)
    .eq('issue_id', issueId);

  if (error) throw error;
  const paths = (data ?? []).map((row) => row.storage_path).filter(Boolean);
  if (paths.length === 0) return;

  const { error: storageError } = await supabase.storage.from(ISSUE_EVIDENCE_BUCKET).remove(paths);
  if (storageError) throw storageError;

  const { error: deleteError } = await supabase
    .from('issue_evidence')
    .delete()
    .eq('organization_id', organizationId)
    .eq('issue_id', issueId);

  if (deleteError) throw deleteError;
}

export async function listIssueEvidence(
  organizationId: string,
  issueId: string,
  stage: EvidenceStage,
): Promise<IssueEvidenceRecord[]> {
  const { data, error } = await supabase
    .from('issue_evidence')
    .select('id, issue_id, organization_id, stage, hypothesis_id, name, kind, mime_type, storage_path, caption, created_by, created_at')
    .eq('organization_id', organizationId)
    .eq('issue_id', issueId)
    .eq('stage', stage)
    .is('hypothesis_id', null)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as IssueEvidenceRecord[];
}

/** Anexos da análise: etapa root e hipótese com id definitivo. */
export async function listHypothesisEvidence(
  organizationId: string,
  issueId: string,
): Promise<IssueEvidenceRecord[]> {
  const { data, error } = await supabase
    .from('issue_evidence')
    .select('id, issue_id, organization_id, stage, hypothesis_id, name, kind, mime_type, storage_path, caption, created_by, created_at')
    .eq('organization_id', organizationId)
    .eq('issue_id', issueId)
    .eq('stage', 'root')
    .not('hypothesis_id', 'is', null)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as IssueEvidenceRecord[];
}

export async function deleteIssueEvidence(record: Pick<IssueEvidenceRecord, 'id' | 'organization_id' | 'storage_path'>): Promise<void> {
  const { error: storageError } = await supabase.storage.from(ISSUE_EVIDENCE_BUCKET).remove([record.storage_path]);
  if (storageError) throw storageError;
  const { error } = await supabase
    .from('issue_evidence')
    .delete()
    .eq('organization_id', record.organization_id)
    .eq('id', record.id);
  if (error) throw error;
}

export async function evidenceSignedUrl(storagePath: string): Promise<string> {
  const { data, error } = await supabase.storage
    .from(ISSUE_EVIDENCE_BUCKET)
    .createSignedUrl(storagePath, SIGNED_URL_SECONDS);
  if (error || !data?.signedUrl) throw error ?? new Error('Não foi possível abrir a evidência.');
  return data.signedUrl;
}

export async function addIssueEvidence(input: {
  organizationId: string;
  issueId: string;
  stage: EvidenceStage;
  hypothesisId?: string | null;
  file: File;
  caption?: string | null;
  createdBy: string;
}): Promise<IssueEvidenceRecord> {
  if (!EVIDENCE_STAGES.includes(input.stage)) throw new Error('Etapa de evidência desconhecida.');

  const { data: issue, error: issueError } = await supabase
    .from('issues')
    .select('id, organization_id, root_cause_analysis')
    .eq('organization_id', input.organizationId)
    .eq('id', input.issueId)
    .single();

  if (issueError || !issue) throw issueError ?? new Error('Problema não encontrado nesta organização.');
  if (issue.organization_id !== input.organizationId) throw new Error('O problema não pertence a esta organização.');

  const hypothesisId = assertHypothesisOnIssue(
    issue.root_cause_analysis as RootCauseAnalysis | null,
    input.hypothesisId,
  );
  const evidenceId = crypto.randomUUID();
  const storagePath = evidenceStoragePath(input.organizationId, input.issueId, evidenceId, input.file.name);
  const kind = evidenceKindForMime(input.file.type || 'application/octet-stream');

  const bytes = await evidenceUploadBytes(input.file);
  const { error: uploadError } = await supabase.storage
    .from(ISSUE_EVIDENCE_BUCKET)
    .upload(storagePath, bytes, {
      cacheControl: '3600',
      upsert: false,
      contentType: input.file.type || 'application/octet-stream',
    });

  if (uploadError) throw uploadError;

  const { data, error } = await supabase
    .from('issue_evidence')
    .insert({
      id: evidenceId,
      issue_id: input.issueId,
      organization_id: input.organizationId,
      stage: input.stage,
      hypothesis_id: hypothesisId,
      name: input.file.name.trim(),
      kind,
      mime_type: input.file.type || 'application/octet-stream',
      storage_path: storagePath,
      caption: input.caption?.trim() || null,
      created_by: input.createdBy,
    })
    .select('*')
    .single();

  if (error) {
    await supabase.storage.from(ISSUE_EVIDENCE_BUCKET).remove([storagePath]);
    throw error;
  }

  return data as IssueEvidenceRecord;
}
